import { type BaseSyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Stack,
} from '@mui/material';
import { LoadingOverlay } from '@travelity/ui/loading-overlay';
import {
    type Attachment,
    convertAttachmentDtoToAttachment,
    useUpdateProductImages,
} from '@travelity/api/services';
import { useForm } from '@travelity/form/use-form';
import { Images } from '@travelity/form/controls';
import type { PackageSidebarItemsProps } from 'src/views/products/PackageItem/components/PackageSidebar/components/types';
import { useToast } from '@/hooks';
import AccordionButtons from '@/views/products/components/accordion-buttons';

type ImagesFormValues = {
    attachments: Attachment[];
};

export const PackageImages = ({ product }: PackageSidebarItemsProps) => {
    const { t } = useTranslation('product');
    const { showSuccess } = useToast();
    const [isEditing, setIsEditing] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const attachments = useMemo(
        () => product.attachments?.items.map(convertAttachmentDtoToAttachment),
        [product.attachments?.items]
    );

    const { mutate: updateImages, isLoading: isUpdatingImages } =
        useUpdateProductImages(attachments || [], {
            onSuccess: () => {
                setIsEditing(false);
                showSuccess('UPDATE_PRODUCT_IMAGES_SUCCESS', {
                    name: product.name,
                });
            },
        });

    const {
        Form: ImagesForm,
        reset: resetImages,
        formState: { errors: imagesErrors },
    } = useForm<ImagesFormValues>({
        defaultValues: {
            attachments: product.attachments?.items.map(
                convertAttachmentDtoToAttachment
            ),
        },
        onSubmit: useCallback(
            (data: { attachments: Attachment[] }, e?: BaseSyntheticEvent) => {
                e?.preventDefault();
                updateImages({
                    items: data.attachments,
                    productId: product.id,
                });
            },
            [product.id, updateImages]
        ),
        mode: 'onChange',
    });

    const onCancel = () => {
        resetImages();
        setIsEditing(false);
    };

    return (
        <ImagesForm key={product.id} id="images-form">
            <Accordion
                disableGutters
                expanded={isExpanded}
                onChange={() => !isEditing && setIsExpanded(!isExpanded)}
            >
                <AccordionSummary>
                    {t('Package Images')}
                    {isExpanded && (
                        <AccordionButtons
                            editing={isEditing}
                            saving={isUpdatingImages}
                            errors={imagesErrors}
                            onEditCancel={onCancel}
                            onEdit={() => setIsEditing(true)}
                        />
                    )}
                </AccordionSummary>
                <AccordionDetails>
                    <Stack
                        spacing={2}
                        sx={{ width: { xs: '100%', sm: '453px' } }}
                    >
                        <Images name="attachments" readOnly={!isEditing} />
                    </Stack>
                    {isUpdatingImages && <LoadingOverlay />}
                </AccordionDetails>
            </Accordion>
        </ImagesForm>
    );
};
